import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import { ConfigProvider, theme } from "antd";
import Calculator from "./pages/Calculator";
import Marketing from "./pages/Discounts";
import AuthGate from "./components/AuthGate";
import {
  createEstimate,
  setClinicData,
  setCustomerData
} from "utilities/redux/slices/currentEstimate";
import { fetchClinics } from "utilities/redux/slices/clinicsSlice";
import { fetchSubsidies } from "utilities/redux/slices/subsidiesSlice";
import axiosInstance from "utilities/axios";

const { defaultAlgorithm, darkAlgorithm } = theme;
function App() {
  const dispatch = useDispatch();

  const clinics = useSelector((state) => state.clinics.data);
  const subsidies = useSelector((state) => state.subsidies.data);
  const customerData = useSelector((state) => state.currentEstimate.customerData);

  const isDarkMode = false;

  useEffect(() => {
    dispatch(fetchClinics());
    dispatch(fetchSubsidies());
  }, [dispatch]);

  useEffect(() => {
    axiosInstance
      .get("/discounts", {
        params: {
          types: ["marketing", "matrix"],
          excludeExpired: true,
        },
      })
      .then((response) => {
        const matrix = response.data.find((code) => code.discountType === "matrix");
        if (matrix) {
          dispatch(setCustomerData({
            ...customerData,
            marketingDiscounts: {
              [matrix.id]: -1,
            },
          }));
        }
      })
  }, [dispatch]);

  useEffect(() => {
    if (clinics.length > 0) {
      const tempClinic = clinics[0].clinicNo;
      const tempProvince = clinics[0].province;

      const ontarioSubsidy = subsidies?.find((subsidy) => subsidy["province"] === "ON");
      const programEligability = ontarioSubsidy && tempProvince === "ON" ? ontarioSubsidy["id"] : null;

      dispatch(setClinicData({
        clinicNo: tempClinic,
        province: tempProvince,
      }));

      dispatch(setCustomerData({
        ...customerData,
        name: null,
        email: null,
        programEligability,
        financingOption: null,
        downPayment: null,
      }));

      dispatch(createEstimate());
    }
  }, [clinics, subsidies, dispatch]);

  return (
    <ConfigProvider
      theme={{
        algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm,
        token: {
          // seed tokens
          borderRadius: 12,
          colorPrimary: "#005DA9",
          // colorInfo: "#005DA9", -- too dark for theme to fir properly
          colorSuccess: "#ACE8B1",
          colorError: "#D93B30",

          colorInfo: "#005DA9",
          fontSize: 16,
          fontSizeHeading1: 64,
          fontSizeHeading2: 48,
          fontSizeHeading3: 36,
          fontWeightStrong: 700,
          fontFamily: "'Proxima Nova', 'Segoe UI', 'Helvatica', sans-serif",

          // map tokens
          colorTextBase: isDarkMode ? "#F1F1F1" : "#49443D",
          colorTextHeading: isDarkMode ? "#F1F1F1" : "#49443D",
          colorText: isDarkMode ? "#F1F1F1" : "#272520", // Light Grey / Black
          colorButtonContainer: isDarkMode ? "#272520" : "#F1F1F1", // Black / Light Grey
          colorBgElevated: isDarkMode ? "#1F1B18" : "#F1F1F1",
          colorBorder: isDarkMode ? "#3A3532" : "rgba(0, 0, 0, 0.2)",
          colorBorderSecondary: isDarkMode ? "#3A3532" : "rgba(0, 0, 0, 0.2)",
          colorTextSecondary: isDarkMode ? "#B0B0B0" : "rgba(0, 0, 0, 0.65)",
          colorTextTertiary: isDarkMode ? "#8A8A8A" : "rgba(0, 0, 0, 0.45)",
          lineWidth: 1,
        },
        components: {
          Grid: {
            colorBgBase: isDarkMode ? "#272520" : "#F1F1F1",
          },
          Layout: {
            siderBg: isDarkMode ? "#1F1B18" : "#F1F1F1",
            lightSiderBg: isDarkMode ? "#272520" : "#F1F1F1",
            colorBgLayout: isDarkMode ? "#1F1B18" : "#F1F1F1",
          },
          Menu: {
            itemBg: isDarkMode ? "#1F1B18" : "#F1F1F1",
            darkItemBg: isDarkMode ? "#272520" : "#F1F1F1",
          },
          Button: {
            defaultBg: isDarkMode ? "#272520" : "#F1F1F1",
            defaultBorderColor: isDarkMode ? "#3A3532" : "rgba(0, 0, 0, 0.2)",
            borderRadius: 12,
          },
          Table: {
            borderRadius: 12,
          },
          Segmented: {
            itemActiveBg: isDarkMode ? "#005DA9" : "#005DA9",
            itemBg: isDarkMode ? "#1F1B18" : "#FFFFFF",
            itemSelectedBg: isDarkMode ? "#005DA9" : "#FFFFFF",
            trackBg: isDarkMode ? "#272520" : "#F1F1F1",
            trackPadding: 4,
          },
        },
      }}
    >
      <div
        style={{
          backgroundColor: isDarkMode ? "#272520" : "#F1F1F1",
        }}
      >
        <Router>
          <AuthGate>
            <Routes>
              <Route exact path="/" element={<Navigate to="/calculator" />} />
              <Route path="calculator" element={<Calculator />} />
              <Route path="marketing/*" element={<Marketing />} />
              <Route path="*" element={<Navigate to="/calculator" />} />
            </Routes>
          </AuthGate>
        </Router>
      </div>
    </ConfigProvider>
  );
}

export default App;